/**
 * Nombre con el que el usuario ve al Super Ejecutivo. Es el único lugar con el nombre del agente:
 * si cambia, solo se cambia aquí (el código siempre dice Super Ejecutivo).
 */
export const NOMBRE_SUPER_EJECUTIVO = 'Kia';

/** Rutas del módulo super-ejecutivo de pyv-services (requerimiento `kia-en-kobra-admin`). */
export const RUTA_CHAT_SUPER_EJECUTIVO = '/chat';
export const RUTA_CONVERSACION_ACTUAL_SUPER_EJECUTIVO = '/conversaciones/actual';
export const RUTA_CERRAR_CONVERSACION_SUPER_EJECUTIVO = '/conversaciones/actual/cerrar';

/**
 * TEMPORAL (requerimiento `super-ejecutivo-graficas`, RN14): mientras dura el piloto, el botón flotante del
 * Super Ejecutivo solo se muestra a los super usuarios con estos idUsuario. Al abrirlo a todos, se quita
 * esta constante y su condición en el layout.
 */
export const IDS_USUARIOS_PILOTO_SUPER_EJECUTIVO: readonly number[] = [14775,18917,21443,22750, 24377, 24242];

/** RN13: largo máximo de un mensaje y desde dónde se avisa que se acerca. */
export const MAXIMO_CARACTERES_SUPER_EJECUTIVO = 4000;
export const CARACTERES_CERCA_DEL_LIMITE_SUPER_EJECUTIVO = 3800;

/** Milisegundos entre consultas mientras el Super Ejecutivo termina una respuesta empezada antes de recargar. */
export const MILISEGUNDOS_ESPERA_PROCESANDO_SUPER_EJECUTIVO = 5000;

/** RN21: milisegundos que se ve el aviso de sesión expirada antes de ir al login. */
export const MILISEGUNDOS_AVISO_SESION_EXPIRADA_SUPER_EJECUTIVO = 3000;

/** Píxeles desde el final en que la lista de mensajes sigue bajando sola con lo nuevo. */
export const PIXELES_CERCA_DEL_FINAL_SUPER_EJECUTIVO = 80;

/** RN5.1: debajo de este ancho el panel ocupa toda la pantalla. */
export const CONSULTA_PANTALLA_ANGOSTA_SUPER_EJECUTIVO = '(max-width: 767.98px)';

/** RN4.1: bienvenida y preguntas de ejemplo con la conversación vacía. */
export const BIENVENIDA_SUPER_EJECUTIVO =
  `Soy ${NOMBRE_SUPER_EJECUTIVO}, analista de operación de Kobra. Pregúntame por tus cuentas, visitas, cobertura o gestores.`;
export const PREGUNTAS_EJEMPLO_SUPER_EJECUTIVO: readonly string[] = [
  '¿Cómo va el seguimiento integral de esta semana?',
  '¿Dónde me faltan gestores?',
  '¿Cuántas cuentas activas tienen el SLA vencido?',
  '¿Cómo les fue a los gestores en las visitas del último mes?',
];

/** Mensajes del panel (requerimiento `kia-en-kobra-admin`). */
export const MENSAJES_SUPER_EJECUTIVO = {
  SUPER_EJECUTIVO_NO_DISPONIBLE: `${NOMBRE_SUPER_EJECUTIVO} no está disponible. Intenta de nuevo en unos minutos.`,
  CONVERSACION_EXPIRADA: 'La conversación anterior expiró; empecé una nueva.',
  CONVERSACION_OCUPADA:
    `${NOMBRE_SUPER_EJECUTIVO} sigue respondiendo tu mensaje anterior; te muestro su respuesta al terminar. Lo que ` +
    'escribiste quedó en el cuadro de texto…',
  CONEXION_PERDIDA: `Se perdió la conexión; te muestro la respuesta en cuanto ${NOMBRE_SUPER_EJECUTIVO} termine…`,
  MENSAJE_MUY_LARGO: `El mensaje no puede pasar de ${MAXIMO_CARACTERES_SUPER_EJECUTIVO.toLocaleString('es-MX')} caracteres.`,
  PENSANDO: `${NOMBRE_SUPER_EJECUTIVO} está pensando…`,
  /** RN21. Mismo texto que `SESION_EXPIRADA` del Super Ejecutivo: con él se reconoce el aviso al final de su respuesta. */
  SESION_EXPIRADA: 'Tu sesión de Kobra Admin expiró. Vuelve a iniciar sesión.',
  PROPUESTA_NO_EJECUTADA_CONVERSACION_EXPIRADA: 'La conversación expiró; la propuesta no se ejecutó.',
  DECISION_NO_ENVIADA: `No se pudo enviar tu decisión porque ${NOMBRE_SUPER_EJECUTIVO} no está disponible; vuelve a autorizar o rechazar la propuesta.`,
} as const;

/** Lenguaje del bloque de código que lleva una gráfica (requerimiento `super-ejecutivo-graficas`, RN1). */
export const LENGUAJE_BLOQUE_GRAFICA_SUPER_EJECUTIVO = 'grafica';

/** Límites del contrato de la gráfica; son los mismos que valida el Super Ejecutivo. */
export const LIMITES_GRAFICA_SUPER_EJECUTIVO = {
  categoriasDona: { min: 2, max: 8 },
  categoriasBarras: { min: 2, max: 30 },
  categoriasLinea: { min: 2, max: 60 },
  seriesBarras: { min: 1, max: 3 },
  seriesLinea: { min: 1, max: 4 },
  seriesApiladas: { min: 2, max: 6 },
  filasMapaCalor: { min: 1, max: 30 },
  columnasMapaCalor: { min: 2, max: 12 },
  cifras: { min: 1, max: 6 },
  puntosDispersion: { min: 3, max: 50 },
  largoTexto: 120,
  largoNota: 300,
} as const;

/**
 * Caso límite: con más categorías que estas, las demás se suman en "Otros". Mientras el contrato limite
 * la dona a 8 categorías y "Otros" exija reunir 2, el agrupamiento solo llega a ejecutarse en las barras:
 * con más de 8 el agente elige barras. La dona conserva el camino por si el contrato crece.
 */
export const MAXIMO_CATEGORIAS_DONA_VISIBLES_SUPER_EJECUTIVO = 7;
export const MAXIMO_BARRAS_VISIBLES_SUPER_EJECUTIVO = 15;
/** Categorías que "Otros" debe reunir para que valga la pena ocultarlas: con una sola no se gana nada. */
export const MINIMO_CATEGORIAS_EN_OTROS_SUPER_EJECUTIVO = 2;
/** Puntos de una línea o de una dispersión hasta los que se escribe cada cifra; con más, solo al pasar el cursor. */
export const MAXIMO_PUNTOS_CON_CIFRA_SUPER_EJECUTIVO = 15;

/** Alto del lienzo por tipo de gráfica, en px; las barras crecen con sus categorías. */
export const ALTO_GRAFICA_SUPER_EJECUTIVO = {
  dona: 240,
  linea: 220,
  dispersion: 240,
  barraPorCategoria: 28,
  minimoBarras: 160,
  /** Espacio para el eje y la leyenda de las barras. */
  margenBarras: 40,
} as const;

/**
 * Colores de las series. El lienzo de chart.js no lee variables CSS, así que repiten los valores de
 * `styles.css` (`--lila-kobra`, `--verde-kobra`, `--kobra-info`, `--kobra-advertencia`, …).
 */
export const PALETA_GRAFICA_SUPER_EJECUTIVO: readonly string[] = [
  '#673ab7',
  '#00c977',
  '#1565c0',
  '#bf360c',
  '#f9a825',
  '#00838f',
  '#8e24aa',
  '#6d4c41',
];
/** Color base del mapa de calor (`--lila-kobra`), en RGB para graduar su intensidad. */
export const RGB_MAPA_CALOR_SUPER_EJECUTIVO = '103, 58, 183';
/** Opacidad del mapa de calor: la mínima (valor 0) y el rango hasta el valor mayor. */
export const ALFA_MAPA_CALOR_SUPER_EJECUTIVO = { minima: 0.08, rango: 0.82 } as const;
/** Color del texto dentro de las barras apiladas y de los ejes (`--kobra-texto-medio`). */
export const COLOR_TEXTO_GRAFICA_SUPER_EJECUTIVO = '#616161';
/** Blanco del lienzo: borde entre las partes de la dona y cifra dentro de las barras apiladas. */
export const COLOR_BLANCO_GRAFICA_SUPER_EJECUTIVO = '#ffffff';

/** Mensajes de las gráficas (RN8.1, RN8.2 y caso límite de "Otros"). `{n}` se reemplaza por la cantidad. */
export const MENSAJES_GRAFICA_SUPER_EJECUTIVO = {
  GRAFICA_NO_VALIDA: 'No se pudo mostrar esta gráfica.',
  GRAFICA_EN_CAMINO: 'Preparando gráfica…',
  GRAFICA_SIN_CERRAR: 'El bloque de la gráfica no se cerró.',
  OTROS: 'Otros ({n})',
} as const;

/** Textos de la descarga de gráficas y tablas. `{unidad}` se reemplaza por la unidad de la gráfica. */
export const TEXTOS_DESCARGA_SUPER_EJECUTIVO = {
  BOTON_GRAFICA: 'Descargar gráfica',
  BOTON_TABLA: 'Descargar tabla',
  IMAGEN: 'Imagen (PNG)',
  EXCEL: 'Excel',
  UNIDAD: 'En {unidad}',
  NO_DESCARGADA: 'No se pudo descargar el archivo. Intenta de nuevo.',
} as const;

/** Encabezados fijos de las tablas del Excel de una gráfica; los demás salen de la gráfica (series, columnas, ejes). */
export const COLUMNAS_EXCEL_GRAFICA_SUPER_EJECUTIVO = {
  CATEGORIA: 'Categoría',
  ETIQUETA: 'Etiqueta',
  VALOR: 'Valor',
  TOTAL: 'Total',
  PORCENTAJE: 'Porcentaje',
  UNIDAD: 'Unidad',
  DETALLE: 'Detalle',
} as const;

/** Formato del Excel: hoja de cada tipo, porcentaje, ancho de columnas (en caracteres) y tipo del archivo. */
export const EXCEL_SUPER_EJECUTIVO = {
  hojaGrafica: 'Gráfica',
  hojaTabla: 'Tabla',
  formatoPorcentaje: '0.0%',
  tamanoTitulo: 14,
  anchoMinimo: 10,
  anchoMaximo: 50,
  margenAncho: 2,
  /** Dígitos con que un número sigue siendo exacto en Excel; uno más largo (un folio, un teléfono) se deja como texto. */
  digitosMaximosNumero: 15,
  tipo: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as const;

/** Densidad de la imagen descargada: el doble de píxeles que en pantalla para que se lea bien al ampliarla. */
export const PIXELES_POR_PUNTO_IMAGEN_SUPER_EJECUTIVO = 2;
/** Margen blanco alrededor de la imagen descargada, en px de pantalla: que no quede pegada al borde al pegarla. */
export const MARGEN_IMAGEN_SUPER_EJECUTIVO = 16;
/**
 * Espacio de más (fracción del ancho y del alto) con que se dibuja una tabla para su imagen: el texto puede salir un
 * poco más ancho que en pantalla y sin él se cortaría la orilla. Lo que sobra se recorta.
 */
export const HOLGURA_IMAGEN_TABLA_SUPER_EJECUTIVO = 0.1;
/** Clase de lo que no sale en la imagen descargada (el botón de descarga). */
export const CLASE_FUERA_DE_IMAGEN_SUPER_EJECUTIVO = 'super-ejecutivo-fuera-de-imagen';

/**
 * Nombre del archivo descargado: solo letras sin acentos, números, guiones y guiones bajos, para que se abra igual en
 * cualquier sistema y se pueda mandar por correo o subir sin que lo cambien.
 */
export const ARCHIVO_SUPER_EJECUTIVO = {
  /** Las gráficas llevan el día; las tablas, sin título, también la hora para distinguirlas. */
  formatoFecha: 'YYYY-MM-DD',
  formatoFechaHora: 'YYYY-MM-DD_HH-mm',
  separador: '_',
  respaldoGrafica: 'Grafica',
  baseTabla: 'Tabla',
  /** Todo lo que no es letra sin acento, número o guion se cambia por el separador. */
  caracteresNoPermitidos: /[^A-Za-z0-9-]+/g,
} as const;
